import type { PerformanceFormat } from "@chessla/shared";
import type { GameLibrarySummary } from "@/features/games/games.api";

export const ALL_FORMATS = "all" as const;
export const ALL_CLOCKS = "all" as const;
export const UNKNOWN_CLOCK = "unknown" as const;

export type PerformanceFormatSelection = PerformanceFormat | typeof ALL_FORMATS;
export type PerformanceCountMode = "analyzed" | "imported";
export type PerformanceSelectionStep = "format" | "clock" | "results";
export interface PerformanceOption<T extends string> {
  value: T;
  label: string;
  count: number;
}
export interface PerformanceSelectionState {
  format: PerformanceFormatSelection | null;
  clock: string | null;
  step: PerformanceSelectionStep;
}
export interface PerformanceQuery {
  format: PerformanceFormat;
  exactTimeControl?: string;
}

const FORMAT_ORDER: readonly PerformanceFormat[] = ["rapid", "blitz", "bullet", "daily"];
const FORMAT_LABELS: Record<PerformanceFormat, string> = {
  rapid: "Rapid",
  blitz: "Blitz",
  bullet: "Bullet",
  daily: "Daily",
};

export const clockLabel = (clock: string): string =>
  clock === UNKNOWN_CLOCK ? "Unknown clock" : clock === ALL_CLOCKS ? "All clocks" : clock;

export const formatOptions = (
  summary: GameLibrarySummary,
  includeAllFormats = false,
  countMode: PerformanceCountMode = "analyzed",
): Array<PerformanceOption<PerformanceFormatSelection>> => {
  const counts = new Map(
    summary.formats.map((format) => [
      format.timeControl,
      countMode === "analyzed" ? format.analyzedCount : format.count,
    ]),
  );
  const formats = FORMAT_ORDER.filter((format) => (counts.get(format) ?? 0) > 0)
    .map((format) => ({ value: format, label: FORMAT_LABELS[format], count: counts.get(format)! }))
    .sort(
      (left, right) => right.count - left.count || FORMAT_ORDER.indexOf(left.value) - FORMAT_ORDER.indexOf(right.value),
    );
  const count = formats.reduce((total, format) => total + format.count, 0);
  if (!count) return [];
  return includeAllFormats ? [...formats, { value: ALL_FORMATS, label: "All", count }] : formats;
};

export const clockOptions = (
  summary: GameLibrarySummary,
  format: PerformanceFormat,
  countMode: PerformanceCountMode = "analyzed",
): Array<PerformanceOption<string>> => {
  const clocks = summary.clocks
    .filter(
      (clock) => clock.timeControl === format && (countMode === "analyzed" ? clock.analyzedCount : clock.count) > 0,
    )
    .map((clock) => ({
      value: clock.exactTimeControl ?? UNKNOWN_CLOCK,
      label: clockLabel(clock.exactTimeControl ?? UNKNOWN_CLOCK),
      count: countMode === "analyzed" ? clock.analyzedCount : clock.count,
    }))
    .sort(
      (left, right) => right.count - left.count || left.label.localeCompare(right.label, undefined, { numeric: true }),
    );
  const count = clocks.reduce((total, clock) => total + clock.count, 0);
  return count ? [...clocks, { value: ALL_CLOCKS, label: "All clocks", count }] : [];
};

export const initialPerformanceSelection = (
  summary: GameLibrarySummary,
  preferredFormat?: string | null,
  countMode: PerformanceCountMode = "analyzed",
): PerformanceSelectionState | null => {
  const formats = formatOptions(summary, false, countMode);
  if (formats.length === 0) return null;
  const format = formats.some(({ value }) => value === preferredFormat) ? (preferredFormat as PerformanceFormat) : null;
  return format ? { format, clock: null, step: "clock" } : { format: null, clock: null, step: "format" };
};

export type PerformanceSelectionAction =
  | { type: "edit"; step: PerformanceSelectionStep }
  | { type: "format"; format: PerformanceFormatSelection }
  | { type: "clock"; clock: string };

export const transitionPerformanceSelection = (
  selection: PerformanceSelectionState,
  action: PerformanceSelectionAction,
  summary: GameLibrarySummary,
  includeAllFormats = false,
  countMode: PerformanceCountMode = "analyzed",
): PerformanceSelectionState => {
  if (action.type === "edit") {
    if (action.step === "clock" && (selection.format == null || selection.format === ALL_FORMATS)) return selection;
    return { ...selection, step: action.step };
  }
  if (action.type === "format") {
    if (!formatOptions(summary, includeAllFormats, countMode).some(({ value }) => value === action.format))
      return selection;
    if (action.format === ALL_FORMATS) return { format: ALL_FORMATS, clock: ALL_CLOCKS, step: "results" };
    return {
      format: action.format,
      clock: null,
      step: "clock",
    };
  }
  if (selection.format == null || selection.format === ALL_FORMATS) return selection;
  if (!clockOptions(summary, selection.format, countMode).some(({ value }) => value === action.clock)) return selection;
  return { ...selection, clock: action.clock, step: "results" };
};

export const queryForSelection = (selection: PerformanceSelectionState): PerformanceQuery | null =>
  selection.step !== "results" || selection.format == null || selection.format === ALL_FORMATS
    ? null
    : {
        format: selection.format,
        ...(selection.clock && selection.clock !== ALL_CLOCKS ? { exactTimeControl: selection.clock } : {}),
      };
